import { supabase, isConfigured } from './supabase';

export interface Affiliate {
  id: string;
  user_id: string;
  email: string;
  code: string;
  commission_rate: number;
  is_active: boolean;
  created_at: string;
  total_referrals: number;
  total_commission_cents: number;
  pending_commission_cents: number;
}

export interface AffiliateCommission {
  id: string;
  affiliate_id: string;
  affiliate_code: string;
  affiliate_email: string;
  referred_user_id: string;
  referred_email: string;
  purchase_id: string;
  purchase_amount_cents: number;
  commission_amount_cents: number;
  status: 'pending' | 'paid' | 'void';
  created_at: string;
}

export async function getAffiliates(): Promise<Affiliate[]> {
  if (!isConfigured) return [];

  try {
    const { data, error } = await supabase.rpc('admin_get_affiliates');
    if (error) {
      console.error('[partners] admin_get_affiliates error:', error);
      return [];
    }
    if (data?.success && data?.affiliates) {
      return data.affiliates as Affiliate[];
    }
    return [];
  } catch (err) {
    console.error('[partners] admin_get_affiliates exception:', err);
    return [];
  }
}

export async function createAffiliate(userId: string): Promise<{ success: boolean; code?: string; error?: string }> {
  if (!isConfigured) return { success: false, error: 'Not configured' };

  try {
    const { data, error } = await supabase.rpc('admin_create_affiliate', { p_user_id: userId });
    if (error) {
      console.error('[partners] admin_create_affiliate error:', error);
      return { success: false, error: error.message };
    }
    return data as { success: boolean; code?: string; error?: string };
  } catch (err) {
    console.error('[partners] admin_create_affiliate exception:', err);
    return { success: false, error: 'Failed to create affiliate' };
  }
}

export async function toggleAffiliate(affiliateId: string, isActive: boolean): Promise<boolean> {
  if (!isConfigured) return false;

  try {
    const { data, error } = await supabase.rpc('admin_toggle_affiliate', {
      p_affiliate_id: affiliateId,
      p_is_active: isActive,
    });
    if (error) {
      console.error('[partners] admin_toggle_affiliate error:', error);
      return false;
    }
    return data?.success === true;
  } catch (err) {
    console.error('[partners] admin_toggle_affiliate exception:', err);
    return false;
  }
}

export async function updateAffiliateCommissionRate(
  affiliateId: string,
  commissionRate: number
): Promise<{ success: boolean; error?: string }> {
  if (!isConfigured) return { success: false, error: 'Not configured' };

  try {
    const { data, error } = await supabase.rpc('admin_update_affiliate_commission_rate', {
      p_affiliate_id: affiliateId,
      p_commission_rate: commissionRate,
    });
    if (error) {
      console.error('[partners] admin_update_affiliate_commission_rate error:', error);
      return { success: false, error: error.message };
    }
    return data as { success: boolean; error?: string };
  } catch (err) {
    console.error('[partners] admin_update_affiliate_commission_rate exception:', err);
    return { success: false, error: 'Failed to update commission rate' };
  }
}

export async function getAffiliateCommissions(
  affiliateId?: string,
  status?: 'pending' | 'paid' | 'void',
  limit: number = 100
): Promise<AffiliateCommission[]> {
  if (!isConfigured) return [];

  try {
    const { data, error } = await supabase.rpc('admin_get_affiliate_commissions', {
      p_affiliate_id: affiliateId || null,
      p_status: status || null,
      p_limit: limit,
    });
    if (error) {
      console.error('[partners] admin_get_affiliate_commissions error:', error);
      return [];
    }
    if (data?.success && data?.commissions) {
      return data.commissions as AffiliateCommission[];
    }
    return [];
  } catch (err) {
    console.error('[partners] admin_get_affiliate_commissions exception:', err);
    return [];
  }
}

export async function bulkUpdateCommissionStatus(
  commissionIds: string[],
  status: 'pending' | 'paid' | 'void'
): Promise<{ success: boolean; updated_count?: number; error?: string }> {
  if (!isConfigured) return { success: false, error: 'Not configured' };

  try {
    const { data, error } = await supabase.rpc('admin_update_affiliate_commission_status', {
      p_commission_ids: commissionIds,
      p_status: status,
    });
    if (error) {
      console.error('[partners] admin_update_affiliate_commission_status error:', error);
      return { success: false, error: error.message };
    }
    return data as { success: boolean; updated_count?: number; error?: string };
  } catch (err) {
    console.error('[partners] admin_update_affiliate_commission_status exception:', err);
    return { success: false, error: 'Failed to update commissions' };
  }
}

export function formatCurrency(cents: number, currency: string = 'GBP'): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
  }).format(cents / 100);
}

export function maskEmail(email: string): string {
  if (!email || !email.includes('@')) return email;
  const [local, domain] = email.split('@');
  const masked = local.length > 2
    ? local[0] + '***' + local.slice(-1)
    : local[0] + '***';
  return `${masked}@${domain}`;
}

export function exportAffiliateCommissionsToCSV(commissions: AffiliateCommission[]): string {
  const headers = [
    'Date',
    'Affiliate Code',
    'Affiliate Email',
    'Referred Email',
    'Purchase Amount',
    'Commission Amount',
    'Status',
  ];

  const rows = commissions.map((c) => [
    new Date(c.created_at).toISOString().split('T')[0],
    c.affiliate_code,
    c.affiliate_email,
    c.referred_email,
    (c.purchase_amount_cents / 100).toFixed(2),
    (c.commission_amount_cents / 100).toFixed(2),
    c.status,
  ]);

  return [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
}

export interface PartnerApplication {
  id: string;
  user_id: string;
  email: string;
  full_name: string | null;
  company_name: string | null;
  website: string | null;
  audience_size: string | null;
  how_promote: string | null;
  social_links: string | null;
  notes: string | null;
  mobile_number: string | null;
  country: string | null;
  business_type: string | null;
  current_tools: string | null;
  social_instagram: string | null;
  social_linkedin: string | null;
  social_youtube: string | null;
  social_tiktok: string | null;
  social_twitter: string | null;
  social_facebook: string | null;
  status: 'pending' | 'approved' | 'rejected';
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string;
}

export async function getPartnerApplications(
  status?: 'pending' | 'approved' | 'rejected',
  limit: number = 100
): Promise<PartnerApplication[]> {
  if (!isConfigured) return [];

  try {
    const { data, error } = await supabase.rpc('admin_get_partner_applications', {
      p_status: status || null,
      p_limit: limit,
    });
    if (error) {
      console.error('[partners] admin_get_partner_applications error:', error);
      return [];
    }
    if (data?.success && data?.applications) {
      return data.applications as PartnerApplication[];
    }
    return [];
  } catch (err) {
    console.error('[partners] admin_get_partner_applications exception:', err);
    return [];
  }
}

export async function reviewPartnerApplication(
  applicationId: string,
  decision: 'approved' | 'rejected'
): Promise<{ success: boolean; affiliate_id?: string; code?: string; error?: string }> {
  if (!isConfigured) return { success: false, error: 'Not configured' };

  try {
    const { data, error } = await supabase.rpc('admin_review_partner_application', {
      p_application_id: applicationId,
      p_decision: decision,
    });
    if (error) {
      console.error('[partners] admin_review_partner_application error:', error);
      return { success: false, error: error.message };
    }
    return data as { success: boolean; affiliate_id?: string; code?: string; error?: string };
  } catch (err) {
    console.error('[partners] admin_review_partner_application exception:', err);
    return { success: false, error: 'Failed to review application' };
  }
}

export async function getPendingPartnerApplicationsCount(): Promise<number> {
  if (!isConfigured) return 0;

  try {
    const { count, error } = await supabase
      .from('partner_applications')
      .select('*', { count: 'exact', head: true })
      .eq('status', 'pending');

    if (error) {
      console.error('[partners] getPendingPartnerApplicationsCount error:', error);
      return 0;
    }
    return count || 0;
  } catch (err) {
    console.error('[partners] getPendingPartnerApplicationsCount exception:', err);
    return 0;
  }
}
